"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "How does revenue management work?",
  answer: "Revenue management uses data to predict demand and adjust pricing. We analyze market trends, competitor rates, and your historical data to ensure your rooms are priced optimally to maximize occupancy and RevPAR."
}, {
  question: "Can you help with OTA listings?",
  answer: "Yes. We optimize your OTA profiles by refining content, managing rate parity, and implementing dynamic pricing strategies. This increases your visibility and conversion rates on platforms like Booking.com and Expedia."
}, {
  question: "What tech stack do you recommend?",
  answer: "We evaluate your current operations to suggest PMS, Channel Managers, and Booking Engines that integrate seamlessly. Our goal is to reduce manual work and improve data accuracy across your entire property."
}, {
  question: "What is a revenue audit?",
  answer: "A revenue audit is a deep dive into your current pricing, distribution, and sales performance. We identify missed opportunities and provide a roadmap to improve your bottom line and operational efficiency."
}, {
  question: "How long until I see results?",
  answer: "While immediate adjustments can improve short-term performance, revenue management is a strategic process. Most clients see measurable improvements in RevPAR and occupancy within 30 to 60 days of implementation."
}, {
  question: "Is this for small hotels too?",
  answer: "Absolutely. Whether you manage a boutique property or a large hotel, data-driven revenue management is essential. We tailor our strategies to fit the specific scale and needs of your unique property."
}, {
  question: "Do you offer ongoing support?",
  answer: "Yes. We provide continuous monitoring and strategy adjustments. We act as your dedicated revenue partner, ensuring your property stays competitive in a constantly changing hospitality market."
}, {
  question: "How do we get started?",
  answer: "We begin with a discovery call to understand your goals. From there, we conduct an initial assessment of your property and propose a customized strategy to drive your revenue growth."
}];
export default function Faq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2463" id="marketingstrategyfaq" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24">
              <div className="font-mono text-xs tracking-widest uppercase text-primary mb-8">
                REVENUE INSIGHTS / 03
              </div>

              <h2 className="font-default font-bold text-foreground mb-10" style={{
              fontSize: "clamp(2.75rem, 5vw, 5rem)",
              lineHeight: "0.95",
              letterSpacing: "-0.03em"
            }}>
                Common
                <br />
                Revenue
                <br />
                Queries.
              </h2>

              <p className="font-serif text-base md:text-lg leading-relaxed mb-10" style={{
              maxWidth: "360px",
              color: "hsl(215 16% 40%)"
            }}>
                We answer the most frequent questions from hotel owners. If you need more clarity, let us talk.
              </p>

              <Button variant="outline" size="lg" className="font-default font-medium tracking-wider uppercase text-xs border-foreground text-foreground bg-transparent hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 px-8 py-6 h-auto">
                Contact Us Today
              </Button>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Accordion type="single" collapsible defaultValue="item-0" className="flex flex-col gap-3">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-0 backdrop-blur-md transition-all duration-300 group" style={{
              backgroundColor: "hsl(0 0% 100%)",
              border: "1px solid hsl(214 15% 88%)",
              boxShadow: "0 4px 20px -2px hsl(222 47% 11% / 0.05)"
            }}>
                  <AccordionTrigger className="hover:no-underline px-6 md:px-8 py-6 md:py-7 min-h-[96px] gap-6 [&>svg]:hidden group-hover:[border-color:hsl(214 15% 88%)]">
                    <div className="flex items-center gap-6 md:gap-8 flex-1 text-left">
                      <span className="font-default font-bold text-xs text-primary shrink-0 tracking-wider">
                        Q.{String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default font-medium text-base md:text-lg text-foreground leading-snug">
                        {faq.question}
                      </span>
                    </div>
                    <Plus className="size-6 text-primary shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                  </AccordionTrigger>
                  <AccordionContent className="px-6 md:px-8 pb-8 pt-0">
                    <div className="pl-0 md:pl-[88px]">
                      <p className="font-serif text-base md:text-[1.0625rem]" style={{
                    lineHeight: "1.75",
                    maxWidth: "600px",
                    color: "hsl(215 16% 40%)"
                  }}>
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}